import React, { useEffect, useState } from 'react';

import {
  curveBasis, curveLinear, curveMonotoneX, curveStep 
} from 'd3-shape';

import { leastIndex, max } from 'd3-array';

import format from 'date-fns/format';

import ceil from 'lodash/ceil';
import flatten from 'lodash/flatten';
import range from 'lodash/range';
import round from 'lodash/round';

export const CURVE_TYPE = {
  LINEAR: 'linear',
  MONOTONE: 'monotone',
  BASIS: 'basis',
  STEP: 'step',
}

const getAllYValues = (datasets = []) => flatten(datasets.map(({ data }) => data.map(({ y }) => y)))

export const getEstimatedYTicks = (datasets, numberOfTicks = 8) => {
  const allYValues = getAllYValues(datasets);

  const maxY = max(allYValues, val => val);
  const roundedMaxY = ceil(maxY / 10) * 10;

  const step = roundedMaxY / numberOfTicks;
  const roundedStep = round(step);

  return range(0, roundedMaxY + 1, roundedStep);
}

export const useRefDimensions = (ref) => {
  const [dimens, setDimens] = useState({ width: 0, height: 0 });

  const observer = React.useRef(
    new ResizeObserver((entries) => {
      // Only care about the first element, we expect one element ot be watched
      const { height, width } = entries[0].contentRect
      setDimens({ height, width });
    })
  )

  useEffect(() => {
    const currentRef = ref.current;
    const currentObserver = observer.current;
    if (currentRef && currentObserver) {
      currentObserver.observe(currentRef)
    }

    return () => {
      if (currentObserver && currentRef) {
        currentObserver.unobserve(currentRef)
      }
    }
  }, [ref, observer])

  return dimens;
}

export const convertMousePointToAxisPoint = (xScale, yScale, mousePosition) => {
  const hoveredDate = xScale.invert(mousePosition[0]);
  const hoveredYValue = yScale.invert(mousePosition[1]);

  return {
    x: hoveredDate,
    y: hoveredYValue,
  };
}

export const roundedAxisValuesByRange = (min, max, steps) => {
  // minimum step size
  const stepsize = (max - min) / steps;
  // increase the step size to a nice boundary
  // for example, 1/10th of the 10^n range that includes it
  const pow = Math.trunc(Math.log10(stepsize)) - 1;
  const truncatedStepSize = Math.trunc(stepsize / 10 ** pow) * 10 ** pow;
  // round min to the same boundary
  const result = [min];

  let truncatedMin = Math.trunc(min / 10 ** pow) * 10 ** pow;
  for (let i = 0; i < steps - 1; i+=1) {
    truncatedMin += truncatedStepSize;
    result.push(truncatedMin);
  }
  result.push(max);
  return result;
}

export const getCurveFunctionFromType = (curveType) => {
  switch (curveType) {
  case CURVE_TYPE.BASIS:
    return curveBasis;
  case CURVE_TYPE.MONOTONE:
    return curveMonotoneX;
  case CURVE_TYPE.STEP:
    return curveStep;
  default:
    return curveLinear;
  }
}

export const getDistanceBetweenTimeSeriesPointAndDate = (d, dateVal, yVal) => {
  const xDist = Math.abs(d.x ** 2 - dateVal ** 2);
  const yDist = Math.abs(d.y ** 2 - yVal ** 2);

  return Math.sqrt(xDist + yDist);
}

export const getDistanceBetweenPointsByYOnly = (d, yVal) => Math.abs(d.y - yVal)

export const getClosestTimeSeriesPoint = (data, selectedPoint) => {
  const closestIndex = leastIndex(data, (a, b) => getDistanceBetweenTimeSeriesPointAndDate(a, selectedPoint.x, selectedPoint.y) - getDistanceBetweenTimeSeriesPointAndDate(b, selectedPoint.x, selectedPoint.y));

  return data[closestIndex];
}

export const formatAxisDate = dateObj => format(dateObj, 'MMM d yyyy k:mm:ss')

export const formatTooltipDate = dateObj => format(dateObj, 'MMM d yyyy k:mm:ss.SSS')
